// Der Abschnitt "Zugehoerigkeit" des Kontaktdetails -- aus kontakt.tsx
// herausgeloest (Party 360, erster Schnitt).

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import {
  Body1,
  Button,
  Caption1,
  Dropdown,
  Field,
  Option,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Subtitle1,
} from '@fluentui/react-components';
import {
  assignableRoles,
  assignContextRole,
  assignedAnnouncement as roleAssignedAnnouncement,
  canEndRole,
  changeButtonLabel,
  changeContextRole,
  changedAnnouncement,
  changeTargets,
  endButtonLabel,
  endContextRole,
  endedAnnouncement as roleEndedAnnouncement,
  fetchContextRoles,
  roleLine,
  ROLE_TYPE_ENDED_NONE_LEFT_MESSAGE,
  unavailableMessage,
  visibilityStatement,
  type AssignmentView,
  type ContextRoleView,
} from '../../context-roles.ts';
import type { PlainMessage } from '../../access.ts';
import { readRoleCatalog, type RoleCatalog } from '../../context-role-names.ts';
import { AppLink } from '../../shell/router.tsx';
import { formatSinceMonth } from '../../relationships.ts';
import { useScreenStyles } from '../../shell/screen-styles.ts';
import { useKontaktStyles } from './stile.ts';
import { beiEscapeSchliessen, PlusSymbol } from './plus-symbol.tsx';
import { ROLE_INPUT_ID, ROLE_STATUS_ID, roleChangeInputId, roleChangeButtonId } from './ids.ts';
import type { KontaktScreenProps } from './typen.ts';
import { NebenKnopf } from '../../shell/nebenknopf.tsx';
import { meldungsIntent } from '../../shell/use-section-commands.ts';
import { artWort } from '../../haus-art.ts';

// --- Zugehoerigkeit (ST-REL-001, erster Schnitt; ADR-0054) -----------------
//
// AUFGABE IN NUTZERSPRACHE: "Ich moechte festhalten, in welcher Rolle dieser
// Kontakt fuer mein Haus arbeitet." Zwei Interaktionen ab dem geoeffneten
// Kontakt: Rolle waehlen, zuordnen. Eine Rolle aendert nicht, wer den Kontakt
// sieht (ADR-0064 Ziffer 2), deshalb gibt es keine Rueckfrage.
//
// DAS HAUS IST KEINE EINGABE: der Server leitet es aus der Berechtigung ab
// und nennt es in der Leseantwort; die Oberflaeche sagt es in Worten.

/**
 * Der Abschnitt "Zugehoerigkeit".
 *
 * GENAU EINE STATUSREGION mit aria-live; in sie kommen nur ERGEBNISSE einer
 * Handlung. Laden, Zustandssatz und Liste stehen ausserhalb -- dieselbe
 * Trennung wie bei den Stichworten (Pruefprotokoll ADR-0052/0053).
 */
/** Der Oeffner des Anlegeformulars (Slice 4c). */
const ROLE_OPENER_ID = 'rolle-hinzufuegen';

export function Zugehoerigkeit({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
  eingebettet = false,
  nurLesen = false,
  onNichtBeschreibbar,
}: KontaktScreenProps & {
  /**
   * Party 360, erster Schnitt: im aufklappbaren Bereich traegt der
   * Accordion-Kopf (h2, id "zugehoerigkeit") die Ueberschrift; der Abschnitt setzt dann
   * keine eigene, und aria-labelledby zeigt auf den Kopf.
   */
  readonly eingebettet?: boolean;
  /** Der Kontakt ist nicht beschreibbar: dieser Abschnitt zeigt nur noch an (Server: 409 party_not_writable). */
  readonly nurLesen?: boolean;
  /** Der Server lehnte mit 409 party_not_writable ab: die Seite liest den Kontakt neu, damit die Leiste den Zustand zeigt. */
  readonly onNichtBeschreibbar?: () => void;
}): ReactElement {
  const styles = useScreenStyles();
  const own = useKontaktStyles();
  const [rollen, setRollen] = useState<readonly ContextRoleView[]>([]);
  /** Namen und vergebbare Rollen aus dem Rollenkatalog; ohne ihn kein geladener Abschnitt. */
  const [katalog, setKatalog] = useState<RoleCatalog>({ names: () => '', assignable: [] });
  const [ziel, setZiel] = useState<AssignmentView | undefined>(undefined);
  const [status, setStatus] = useState<'laedt' | 'geladen' | 'abgelehnt'>('laedt');
  /** Das Anlegeformular ist zu, bis jemand "Rolle hinzufügen" waehlt (Slice 4c; kein gemerkter Zustand). */
  const [formOffen, setFormOffen] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<string | undefined>(undefined);
  const [feldfehler, setFeldfehler] = useState<string | undefined>(undefined);
  /** Ergebnis einer HANDLUNG -- getrennt vom Ladefehler (Codex-Cloud-Review #262). */
  const [message, setMessage] = useState<PlainMessage | undefined>(undefined);
  // Ob die Meldung einen offenen Ausgang nennt; gesetzt dort, wo der Satz gewaehlt wird.
  const [offen, setOffen] = useState(false);
  /** Der Ladefehler; ein gelungenes Neuladen raeumt ihn und nur ihn ab. */
  const [ladefehler, setLadefehler] = useState<PlainMessage | undefined>(undefined);
  const [bestaetigung, setBestaetigung] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  // Blieb ein Zuordnen oder Beenden ohne Antwort, kann es gewirkt haben: dann
  // ist "schon zugeordnet" bzw. "schon beendet" beim naechsten Versuch ein Erfolg.
  const offenerVersuch = useRef<string | undefined>(undefined);
  /** Die Rolle, die gerade geaendert wird (ADR-0057) -- hoechstens eine zugleich. */
  const [inAenderung, setInAenderung] = useState<string | undefined>(undefined);
  const [neueRolle, setNeueRolle] = useState<string | undefined>(undefined);
  const [aenderungsfehler, setAenderungsfehler] = useState<string | undefined>(undefined);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const [fokusAnfordern, setFokusAnfordern] = useState(0);
  // Fokus in die Auswahl erst nach dem Rendern: waehrend des Sendens ist sie gesperrt.
  const [fokusAuswahl, setFokusAuswahl] = useState(0);
  useEffect(() => {
    if (fokusAuswahl > 0) document.getElementById(ROLE_INPUT_ID)?.focus();
  }, [fokusAuswahl]);
  const laufRef = useRef(0);

  useEffect(() => {
    if (fokusAnfordern > 0) {
      statusRef.current?.focus();
    }
  }, [fokusAnfordern]);

  /** Laedt Rollen und Katalog; gibt zurueck, wie viele Rollen im Haus danach noch frei sind (undefined: nicht geladen). */
  const laden = useCallback(async (): Promise<number | undefined> => {
    const lauf = laufRef.current + 1;
    laufRef.current = lauf;
    const gueltig = (): boolean => laufRef.current === lauf;
    setStatus('laedt');
    // Der ganze Abruf im try: ein Abbruch auf Transportebene darf den
    // Abschnitt nicht im Ladezustand zuruecklassen (Muster Stichworte).
    try {
      const token = await accessToken();
      if (!gueltig()) return;
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const [stand, rollenkatalog] = await Promise.all([
        fetchContextRoles({ baseUrl: apiBaseUrl, accessToken: token, partyId }),
        readRoleCatalog({ baseUrl: apiBaseUrl, accessToken: token }),
      ]);
      if (!gueltig()) return;
      if (stand.outcome === 'not-found') {
        setStatus('abgelehnt');
        onReauthenticate();
        return;
      }
      if (stand.outcome === 'rejected') {
        setStatus('abgelehnt');
        setLadefehler(stand.message);
        return;
      }
      // Ohne Katalog fehlen die Namen: ein ehrlicher Ladefehler statt Kennungen (Entscheidung in ST-REL-001).
      if (rollenkatalog.outcome === 'rejected') {
        setStatus('abgelehnt');
        setLadefehler(rollenkatalog.message);
        return;
      }
      setKatalog(rollenkatalog.catalog);
      setRollen(stand.contextRoles);
      setZiel(stand.assignment);
      setLadefehler(undefined);
      setStatus('geladen');
      return stand.assignment.available
        ? assignableRoles(stand.contextRoles, stand.assignment.operatingContextId, rollenkatalog.catalog.assignable).length
        : 0;
    } catch {
      if (!gueltig()) return;
      setLadefehler({
        headline: 'Die Rollen sind gerade nicht erreichbar.',
        explanation: 'Es kam keine Antwort.',
        nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
        retryable: true,
      });
      setStatus('abgelehnt');
    }
  }, [apiBaseUrl, accessToken, partyId, onReauthenticate]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const haus = ziel !== undefined && ziel.available ? ziel.operatingContextDisplayName : undefined;
  const hausArt = ziel !== undefined && ziel.available ? ziel.operatingContextKind : null;
  const zielKennung = ziel !== undefined && ziel.available ? ziel.operatingContextId : undefined;
  const auswahl = zielKennung === undefined ? [] : assignableRoles(rollen, zielKennung, katalog.assignable);
  const namen = katalog.names;
  // Ohne Auswahl gibt es weder Oeffner noch Formular; es darf spaeter nicht von
  // selbst wieder aufspringen (Experience-Review 4c, P3-2).
  useEffect(() => {
    if (status === 'geladen' && auswahl.length === 0) {
      setFormOffen(false);
    }
  }, [status, auswahl.length]);
  const senden = async (rolle: string): Promise<void> => {
    if (zielKennung === undefined) {
      // Ohne eindeutiges Haus gibt es keine Auswahl und damit keinen Aufruf.
      return;
    }
    setBusy(true);
    setOffen(false);
    setMessage(undefined);
    setBestaetigung(undefined);
    let ergebnis;
    // Token UND Aufruf im try: auch ein gescheiterter Tokenabruf darf die
    // Aktion nicht dauerhaft sperren (Codex-Cloud-Review #262, P2).
    try {
      const token = await accessToken();
      if (token === undefined) {
        setBusy(false);
        onReauthenticate();
        return;
      }
      ergebnis = await assignContextRole({
        baseUrl: apiBaseUrl,
        accessToken: token,
        partyId,
        roleCode: rolle,
        // Das Haus, dessen Namen der Abschnitt zeigte: weicht der Server ab,
        // wird nichts geschrieben (Codex-Cloud-Review #262, P2).
        expectedOperatingContextId: zielKennung,
        art: hausArt,
      });
    } catch {
      // Abbruch auf Transportebene: die Auswahl bleibt stehen, die Schaltflaeche
      // wird wieder frei. Ob der Server geschrieben hat, klaert der naechste
      // Versuch: eine schon zugeordnete Rolle ist ein 409 und kein zweiter Eintrag.
      setBusy(false);
      offenerVersuch.current = `zuordnen:${rolle}`;
      setOffen(true);
      setMessage({
        headline: 'Ob die Rolle zugeordnet wurde, ist offen.',
        explanation: 'Die Antwort kam nicht an.',
        nextStep: 'Versuche es erneut. Doppelt zugeordnet wird dabei nichts. Die Auswahl bleibt erhalten.',
        retryable: true,
      });
      setFokusAnfordern((zahl) => zahl + 1);
      return;
    }
    setBusy(false);
    const nachAbbruch = offenerVersuch.current === `zuordnen:${rolle}`;
    offenerVersuch.current = undefined;
    if (ergebnis.outcome === 'assigned' || (nachAbbruch && ergebnis.outcome === 'rejected' && ergebnis.reasonCode === 'context_role_duplicate')) {
      setGewaehlt(undefined);
      setFormOffen(false);
      setBestaetigung(roleAssignedAnnouncement(namen(rolle), haus ?? artWort(hausArt, 'deinen')));
      setFokusAnfordern((zahl) => zahl + 1);
      await laden();
      return;
    }
    if (ergebnis.outcome === 'not-found') {
      // Der Kontakt ist inzwischen fuer dieses Haus nicht mehr sichtbar --
      // derselbe Rueckweg wie in den Nachbarabschnitten.
      setStatus('abgelehnt');
      onReauthenticate();
      return;
    }
    if (ergebnis.outcome === 'invalid') {
      // Die Auswahl passt nicht mehr zum Server -- der Abschnitt laedt selbst
      // neu, statt die Nutzerin zum Neuladen der Seite zu schicken
      // (Experience-Review 25.09.2026, Befund 2).
      setGewaehlt(undefined);
      await laden();
      setFeldfehler('Diese Rolle wurde nicht angenommen. Die Auswahl ist neu geladen; wähle erneut.');
      document.getElementById(ROLE_INPUT_ID)?.focus();
      return;
    }
    if (ergebnis.reasonCode === 'context_role_type_not_assignable') {
      // Die Rolle wurde zwischen Laden und Speichern beendet: der Katalog laedt neu,
      // die Rolle verschwindet aus der Auswahl, der Satz steht am Feld und der
      // Fokus geht hinein (Entscheidung in ST-REL-001).
      const { headline, explanation, nextStep } = ergebnis.message;
      setGewaehlt(undefined);
      const frei = await laden();
      if (frei === undefined) return;
      if (frei === 0) {
        // Es war die letzte freie Rolle: das Formular schliesst, der Satz geht
        // in die Statusregion und der Fokus dorthin (Entscheidung in ST-REL-001).
        setOffen(false);
        setMessage(ROLE_TYPE_ENDED_NONE_LEFT_MESSAGE);
        setFokusAnfordern((zahl) => zahl + 1);
        return;
      }
      setFeldfehler(`${headline} ${explanation} ${nextStep}`);
      setFokusAuswahl((zahl) => zahl + 1);
      return;
    }
    setOffen(false);
    setMessage(ergebnis.message);
    setFokusAnfordern((zahl) => zahl + 1);
    if (ergebnis.reasonCode === 'party_not_writable') onNichtBeschreibbar?.();
    if (ergebnis.reasonCode === 'context_role_duplicate' || ergebnis.reasonCode === 'context_role_context_changed') {
      setGewaehlt(undefined);
      await laden();
    }
  };

  // --- Aendern und Beenden (ST-REL-001, zweiter Schnitt; ADR-0057) ----------
  //
  // Beides aendert nie, wer den Kontakt sehen kann (R-S2-1); deshalb kein
  // Bestaetigungsdialog -- beides ist in diesem Abschnitt umkehrbar. Das
  // Ergebnis geht in DIESELBE Statusregion, der Fokus folgt ihm.
  const aenderungSchliessen = (rolle?: ContextRoleView): void => {
    setInAenderung(undefined);
    setNeueRolle(undefined);
    setAenderungsfehler(undefined);
    if (rolle !== undefined) {
      // Zurueck an die Schaltflaeche, von der die Aenderung ausging.
      window.setTimeout(() => document.getElementById(roleChangeButtonId(rolle.contextRoleAssignmentId))?.focus(), 0);
    }
  };

  const lebenszyklus = async (rolle: ContextRoleView, naechste: string | undefined): Promise<void> => {
    setBusy(true);
    setOffen(false);
    setMessage(undefined);
    setBestaetigung(undefined);
    let ergebnis;
    try {
      const token = await accessToken();
      if (token === undefined) {
        setBusy(false);
        onReauthenticate();
        return;
      }
      ergebnis =
        naechste === undefined
          ? await endContextRole({ baseUrl: apiBaseUrl, accessToken: token, partyId, role: rolle })
          : await changeContextRole({ baseUrl: apiBaseUrl, accessToken: token, partyId, role: rolle, nextRoleCode: naechste, names: namen });
    } catch {
      // Abbruch auf Transportebene: ob der Server geschrieben hat, klaert beim
      // Beenden der naechste Versuch -- ein schon gespeichertes Ende ist dann ein
      // Erfolg. Beim Aendern traegt die Wiederholung den alten Stand; dort zeigt
      // erst die neu geladene Seite, was gilt.
      setBusy(false);
      if (naechste === undefined) offenerVersuch.current = `beenden:${rolle.contextRoleAssignmentId}`;
      setOffen(true);
      setMessage(
        naechste === undefined
          ? {
              headline: 'Ob die Rolle beendet wurde, ist offen.',
              explanation: 'Die Antwort kam nicht an.',
              nextStep: 'Versuche es erneut. Doppelt beendet wird dabei nichts.',
              retryable: true,
            }
          : {
              headline: 'Ob die Rolle geändert wurde, ist offen.',
              explanation: 'Die Antwort kam nicht an.',
              nextStep: 'Lade die Seite neu. Dann siehst du, was gilt.',
              retryable: false,
            },
      );
      setFokusAnfordern((zahl) => zahl + 1);
      return;
    }
    setBusy(false);
    const endeNachAbbruch = naechste === undefined && offenerVersuch.current === `beenden:${rolle.contextRoleAssignmentId}`;
    offenerVersuch.current = undefined;
    if (
      ergebnis.outcome === 'ended' ||
      ergebnis.outcome === 'changed' ||
      (endeNachAbbruch && ergebnis.outcome === 'rejected' && ergebnis.reasonCode === 'context_role_already_ended')
    ) {
      aenderungSchliessen();
      setBestaetigung(naechste === undefined ? roleEndedAnnouncement(rolle, namen) : changedAnnouncement(rolle, naechste, namen));
      setFokusAnfordern((zahl) => zahl + 1);
      await laden();
      return;
    }
    if (ergebnis.outcome === 'not-found') {
      setStatus('abgelehnt');
      onReauthenticate();
      return;
    }
    if (ergebnis.outcome === 'invalid') {
      // Die Auswahl passt nicht mehr zum Server: neu laden, am Feld sagen.
      await laden();
      if (naechste !== undefined) {
        setNeueRolle(undefined);
        setAenderungsfehler('Diese Rolle wurde nicht angenommen. Die Auswahl ist neu geladen; wähle erneut.');
        document.getElementById(roleChangeInputId(rolle.contextRoleAssignmentId))?.focus();
      }
      return;
    }
    // Jeder Konflikt: nichts ist geschrieben. Der Abschnitt laedt den Stand
    // neu, damit Liste und Auswahl stimmen, und die Meldung nennt die Haelfte.
    aenderungSchliessen();
    setOffen(false);
    setMessage(ergebnis.message);
    setFokusAnfordern((zahl) => zahl + 1);
    await laden();
  };

  const aendernSenden = (event: FormEvent, rolle: ContextRoleView): void => {
    event.preventDefault();
    if (neueRolle === undefined) {
      setAenderungsfehler('Wähle zuerst die neue Rolle aus.');
      document.getElementById(roleChangeInputId(rolle.contextRoleAssignmentId))?.focus();
      return;
    }
    void lebenszyklus(rolle, neueRolle);
  };

  // Abbrechen und Escape: das Formular schliessen, Eingaben verwerfen, Fokus an den Oeffner.
  const formSchliessen = (): void => {
    setFormOffen(false);
    setGewaehlt(undefined);
    setFeldfehler(undefined);
    window.setTimeout(() => document.getElementById(ROLE_OPENER_ID)?.focus(), 0);
  };

  const zuordnen = (event: FormEvent): void => {
    event.preventDefault();
    if (gewaehlt === undefined) {
      // Die freundlichere Kontrolle zuerst, nie die einzige: der Server
      // antwortet auf denselben Fall mit 422 und Feldpfad.
      setFeldfehler('Wähle zuerst eine Rolle aus.');
      // Der Fokus geht ins Feld: dort liest der Screenreader die Meldung
      // ueber aria-describedby vor (Experience-Review 25.09.2026, Befund 1).
      document.getElementById(ROLE_INPUT_ID)?.focus();
      return;
    }
    void senden(gewaehlt);
  };

  // "+ Rolle" am Ende der Ueberschrift (v4 "+ Stichwort", DESIGN 13.10.): sichtbar
  // kurz, der Name traegt den Bezug und beginnt mit dem sichtbaren Wort (WCAG 2.5.3).
  // Der Oeffner verschwindet, solange das Formular offen ist; er traegt darum kein
  // aria-expanded (Bauvorgabe 4c).
  const oeffner =
    status === 'geladen' && haus !== undefined && auswahl.length > 0 && !formOffen && !nurLesen ? (
      <Button
        id={ROLE_OPENER_ID}
        appearance="subtle"
        size="small"
        className={own.abschnittPlus}
        icon={<PlusSymbol />}
        aria-label="Rolle hinzufügen"
        onClick={() => {
          setFormOffen(true);
          window.setTimeout(() => document.getElementById(ROLE_INPUT_ID)?.focus(), 0);
        }}
      >
        Rolle
      </Button>
    ) : undefined;

  return (
    <section className={own.gruppe} aria-labelledby="zugehoerigkeit">
      {eingebettet ? (
        oeffner
      ) : (
        <div className={own.abschnittKopf} data-abschnittkopf="">
          <Subtitle1 as="h2" id="zugehoerigkeit">
            Rollen
          </Subtitle1>
          {oeffner}
        </div>
      )}

      {status === 'laedt' ? <Spinner labelPosition="after" label="Rollen werden geladen" /> : undefined}

      {/* Der Zustand in Worten (R8) -- AUSSERHALB der Statusregion: er
          beschreibt den Bestand, nicht das Ergebnis einer Handlung. */}
      {status === 'geladen' ? <Caption1 className={own.hinweis}>{visibilityStatement(rollen.length)}</Caption1> : undefined}

      {status === 'geladen' && rollen.length > 0 ? (
        <ul className={own.liste} aria-label="Rollen dieses Kontakts">
          {rollen.map((rolle) => {
            const ziele = changeTargets(rollen, rolle, katalog.assignable);
            const wirdGeaendert = inAenderung === rolle.contextRoleAssignmentId;
            return (
              <li key={rolle.contextRoleAssignmentId} className={own.eintrag}>
                <Body1 className={own.text}>{roleLine(rolle, formatSinceMonth(rolle.validFrom), namen)}</Body1>
                {/* Zwei unauffaellige Aktionen je Rolle (subtle): die
                    Hauptaktion des Bildschirms bleibt "Notiz festhalten"
                    (NFR-UX-004). "Beenden" nur, wenn das Haus danach noch
                    eine Rolle hat (R-S2-1) -- sonst waere es eine Aktion, die
                    immer abgewiesen wird. */}
                {nurLesen ? undefined : !wirdGeaendert ? (
                  <span className={own.merkmale}>
                    <Button
                      id={roleChangeButtonId(rolle.contextRoleAssignmentId)}
                      appearance="subtle"
                      size="small"
                      disabled={busy}
                      aria-label={changeButtonLabel(rolle, namen)}
                      onClick={() => {
                        setInAenderung(rolle.contextRoleAssignmentId);
                        setNeueRolle(undefined);
                        setAenderungsfehler(undefined);
                        window.setTimeout(() => document.getElementById(roleChangeInputId(rolle.contextRoleAssignmentId))?.focus(), 0);
                      }}
                    >
                      Ändern
                    </Button>
                    {canEndRole(rollen, rolle) ? (
                      <Button
                        appearance="subtle"
                        size="small"
                        disabled={busy}
                        aria-label={endButtonLabel(rolle, namen)}
                        onClick={() => void lebenszyklus(rolle, undefined)}
                      >
                        Beenden
                      </Button>
                    ) : undefined}
                  </span>
                ) : ziele.length === 0 ? (
                  <span className={own.merkmale}>
                    <Caption1 id={roleChangeInputId(rolle.contextRoleAssignmentId)} tabIndex={-1}>
                      Alle anderen Rollen sind für {rolle.operatingContextDisplayName} bereits zugeordnet.
                    </Caption1>
                    <NebenKnopf size="small" onClick={() => aenderungSchliessen(rolle)}>
                      Abbrechen
                    </NebenKnopf>
                  </span>
                ) : (
                  <form className={own.wahl} noValidate onSubmit={(event) => aendernSenden(event, rolle)}>
                    <Field
                      label="Neue Rolle"
                      hint={`Die bisherige Rolle endet heute; beide bleiben mit Beginn und Ende gespeichert.`}
                      validationMessage={aenderungsfehler ?? null}
                      validationState={aenderungsfehler === undefined ? 'none' : 'error'}
                    >
                      <Dropdown
                        className={own.auswahlfeld}
                        id={roleChangeInputId(rolle.contextRoleAssignmentId)}
                        disabled={busy}
                        placeholder="Rolle wählen"
                        value={neueRolle === undefined ? '' : namen(neueRolle)}
                        selectedOptions={neueRolle === undefined ? [] : [neueRolle]}
                        onOptionSelect={(_, data) => {
                          setNeueRolle(data.optionValue);
                          if (aenderungsfehler !== undefined) setAenderungsfehler(undefined);
                        }}
                      >
                        {ziele.map((code) => (
                          <Option key={code} value={code}>
                            {namen(code)}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                    <div className={styles.actions}>
                      <Button appearance="secondary" type="submit" disabled={busy}>
                        Rolle ändern
                      </Button>
                      <NebenKnopf disabled={busy} onClick={() => aenderungSchliessen(rolle)}>
                        Abbrechen
                      </NebenKnopf>
                    </div>
                  </form>
                )}
              </li>
            );
          })}
        </ul>
      ) : undefined}

      {status === 'geladen' && ziel !== undefined && !ziel.available ? (
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>{unavailableMessage(ziel.unavailableReason).headline}</MessageBarTitle>
            <div>{unavailableMessage(ziel.unavailableReason).explanation}</div>
            <div>{unavailableMessage(ziel.unavailableReason).nextStep}</div>
          </MessageBarBody>
        </MessageBar>
      ) : undefined}

      {status === 'geladen' && haus !== undefined && katalog.assignable.length === 0 && !nurLesen ? (
        <Caption1 className={own.hinweis}>
          Noch keine Rolle ist vergebbar. Neue Rollen schlägst du im <AppLink to="/rollenkatalog">Rollenkatalog</AppLink> vor.
        </Caption1>
      ) : status === 'geladen' && haus !== undefined && auswahl.length === 0 ? (
        <Caption1 className={own.hinweis}>Alle Rollen sind für {haus} bereits zugeordnet.</Caption1>
      ) : undefined}

      {status === 'geladen' && haus !== undefined && auswahl.length > 0 && formOffen && !nurLesen ? (
        <div role="group" aria-label="Rolle hinzufügen">
          <form className={own.wahl} noValidate onSubmit={zuordnen} onKeyDown={(event) => beiEscapeSchliessen(event, formSchliessen)}>
            <Field
              label="Rolle"
              hint={`Die Rolle gilt für ${haus}. Beginn und Zuordnung setzt LIAISON.`}
              validationMessage={feldfehler ?? null}
              validationState={feldfehler === undefined ? 'none' : 'error'}
            >
              <Dropdown
                id={ROLE_INPUT_ID}
                disabled={busy}
                placeholder="Rolle wählen"
                value={gewaehlt === undefined ? '' : namen(gewaehlt)}
                selectedOptions={gewaehlt === undefined ? [] : [gewaehlt]}
                onOptionSelect={(_, data) => {
                  setGewaehlt(data.optionValue);
                  if (feldfehler !== undefined) setFeldfehler(undefined);
                }}
              >
                {auswahl.map((code) => (
                  <Option key={code} value={code}>
                    {namen(code)}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <div className={styles.actions}>
              {/* secondary UND NICHT primary: die EINE Hauptaktion des
                  Bildschirms bleibt "Notiz festhalten" (NFR-UX-004, Kopf dieser
                  Datei); dies ist die einzige Aktion ihres Abschnitts. */}
              <Button appearance="secondary" type="submit" disabled={busy}>
                Rolle zuordnen
              </Button>
              <NebenKnopf
                disabled={busy}
                onClick={formSchliessen}
              >
                Abbrechen
              </NebenKnopf>
            </div>
          </form>
        </div>
      ) : undefined}

      <div
        aria-live="polite"
        role="status"
        aria-label="Status: Rollen"
        id={ROLE_STATUS_ID}
        ref={statusRef}
        tabIndex={-1}
        className={own.hinweis}
      >
        {bestaetigung !== undefined ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>Gespeichert</MessageBarTitle>
              <div>{bestaetigung}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {message !== undefined ? (
          <MessageBar intent={meldungsIntent(message, offen)}>
            <MessageBarBody>
              <MessageBarTitle>{message.headline}</MessageBarTitle>
              <div>{message.explanation}</div>
              <div>{message.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {ladefehler !== undefined ? (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{ladefehler.headline}</MessageBarTitle>
              <div>{ladefehler.explanation}</div>
              <div>{ladefehler.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
      </div>

      {status === 'abgelehnt' && ladefehler?.retryable === true ? (
        <div className={styles.actions}>
          <Button appearance="secondary" disabled={busy} onClick={() => void laden()}>
            Rollen erneut laden
          </Button>
        </div>
      ) : undefined}
    </section>
  );
}
